import { logger } from '@/config/logger'
import {
  Description,
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { signUp } from 'aws-amplify/auth'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import { useEffect, useState } from 'react'
import { BiQuestionMark } from 'react-icons/bi'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import AWSWAFCaptchaModal from '../components/AWSCaptchaModal'
import Password from '../components/Password'
import { SignupSchema } from '../utils/schemas'

export const Signup = () => {
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [loading, setLoading] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const invitationEmailParam = searchParams.get('invitation-email') // Extract the invitation email from URL
  const invitationEmail = invitationEmailParam
    ? decodeURIComponent(invitationEmailParam)
    : null // Decode the email

  const isPersonalEmailCheckEnabled = !invitationEmail // Disable personal email check if invitation token exists

  const [personalEmailDomains, setPersonalEmailDomains] = useState<string[]>([])

  // Function to load the list of personal email domains from a .txt file which is in public/ folder
  useEffect(() => {
    const loadEmailDomains = async () => {
      try {
        const response = await fetch('/domains_to_block.json')
        const data = await response.json()
        const domains = data
          .map((domain: string) => domain.trim())
          .filter((domain: string) => domain !== '')
        setPersonalEmailDomains(domains)
      } catch (error) {
        logger.error('Error loading email domains:', error)
      }
    }

    loadEmailDomains()
  }, [])

  const isPersonalEmail = (email: string) => {
    const domain = email.substring(email.lastIndexOf('@') + 1).toLowerCase()
    return personalEmailDomains.includes(domain)
  }

  const createCognitoUser = async (formValues: any) => {
    setLoading(true)
    const clientMetadataValuesToSend = [
      'first_name',
      'last_name',
      'email',
      'password',
      'role',
    ]
    const clientMetadata: any = {}
    clientMetadataValuesToSend.forEach((value) => {
      clientMetadata[value] = formValues[value]
    })

    if (invitationEmail) {
      clientMetadata['isInvitation'] = 'true' // Add this attribute to force pre sign up lambda to check that the user is in the invitation list
    }

    const userAttributes = {
      email: formValues.email,
    }

    try {
      await sendSignUp(formValues.email, formValues.password, {
        userAttributes,
        clientMetadata,
        validationData: userAttributes,
      })
      navigate('/login', { state: { justSignedUp: true } })
    } catch (e: any) {
      setErrorMessage(e.message)
      setHasError(true)
      setLoading(false)
    }
  }

  const sendSignUp = async (username: any, password: any, options: any) => {
    try {
      await signUp({
        username,
        password,
        options,
      })
    } catch (e: any) {
      if (e.message === 'Network error') {
        window.shouldShowCaptcha = true
        await sendSignUp(username, password, options)
      } else {
        throw e
      }
    }
  }

  const retrieveEmailErrorMessage = (value: string) => {
    let error
    if (!value) {
      error = 'Required'
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
      error = 'Invalid email address'
    } else if (isPersonalEmailCheckEnabled && isPersonalEmail(value)) {
      error = 'A professional email address is required'
    }
    return error
  }

  return (
    <>
      <Dialog
        open={hasError}
        onClose={() => setHasError(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <DialogPanel className="mx-auto max-w-sm rounded-lg p-10 bg-primary-50">
            <DialogTitle>Error while signing up</DialogTitle>
            <Description className="py-4">{errorMessage}</Description>

            <button
              className="ircam-button button button-regular button-dark block mx-auto"
              type="button"
              onClick={() => setHasError(false)}
            >
              OK
            </button>
          </DialogPanel>
        </div>
      </Dialog>
      <div className="w-4/6 mx-auto mt-28">
        <h1 className="text-4xl text-center my-8 font-light">
          Create an account
        </h1>
        <div className="w-full grid grid-cols-2 my-8">
          <div className="text-sm text-primary-600">
            <Link to="/login">&lt; Back</Link>
          </div>
          <div className="text-sm text-right text-primary-400">
            * Mandatory fields
          </div>
        </div>
        <Formik
          initialValues={{
            first_name: '',
            last_name: '',
            email: invitationEmail || '',
            legal_agree: false,
            password: '',
            password_confirmation: '',
            role: 'placeholder',
          }}
          validationSchema={SignupSchema}
          onSubmit={createCognitoUser}
        >
          <Form>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="text-sm block w-full">
                  First name*
                  <Field
                    type="text"
                    id="first_name"
                    name="first_name"
                    className="ircam-input my-1 w-full"
                  />
                  <ErrorMessage
                    name="first_name"
                    className="text-ircamprimary-accent"
                    component="span"
                  />
                </label>
              </div>
              <div>
                <label htmlFor="last_name" className="text-sm block w-full">
                  Last name*
                  <Field
                    type="text"
                    id="last_name"
                    name="last_name"
                    className="ircam-input my-1 w-full"
                  />
                  <ErrorMessage
                    name="last_name"
                    className="text-ircamprimary-accent"
                    component="span"
                  />
                </label>
              </div>
            </div>

            <div className="my-4">
              <label htmlFor="email" className="text-sm ">
                <div className="flex items-center">
                  {isPersonalEmailCheckEnabled
                    ? 'Professional email address*'
                    : 'Email address*'}
                  {isPersonalEmailCheckEnabled && (
                    <div className="relative group ml-2">
                      <a data-tooltip-id="my-tooltip-children-multiline">
                        <BiQuestionMark
                          size={15}
                          style={{
                            borderRadius: '50%',
                            border: '1px solid black',
                          }}
                        />
                      </a>
                      <Tooltip
                        id="my-tooltip-children-multiline"
                        style={{
                          backgroundColor: '#21222e',
                          borderRadius: '10px',
                        }}
                        noArrow
                      >
                        <div
                          style={{ display: 'flex', flexDirection: 'column' }}
                        >
                          <span>
                            A <strong>business email</strong> is{' '}
                            <strong>required </strong>
                            to use our services. Contact us if you don't have a
                            business email.
                          </span>
                        </div>
                      </Tooltip>
                    </div>
                  )}
                </div>

                <Field
                  type="text"
                  id="email"
                  name="email"
                  className="ircam-input my-1 w-full"
                  disabled={invitationEmail ? true : false}
                  validate={retrieveEmailErrorMessage}
                />
                <ErrorMessage
                  name="email"
                  className="text-ircamprimary-accent"
                  component="span"
                />
              </label>
            </div>

            <div className="my-4">
              <label htmlFor="role" className="text-sm">
                Role*
                <Field
                  as="select"
                  id="role"
                  name="role"
                  className="ircam-input my-1 w-full"
                >
                  <option value="placeholder">Select your role</option>
                  <option value="developer">Developer</option>
                  <option value="techlead">Tech/Engineering team lead</option>
                  <option value="cto">CIO / CTO / IT manager</option>
                  <option value="csuite">CEO/management</option>
                  <option value="business">
                    Product manager / marketing / sales
                  </option>
                </Field>
                <ErrorMessage
                  name="role"
                  className="text-ircamprimary-accent"
                  component="span"
                />
              </label>
            </div>

            <div className="my-4">
              <label htmlFor="password" className="text-sm w-full my-4 block">
                Password*
                <Field
                  as={Password}
                  id="password"
                  name="password"
                  checkValidity
                />
                <ErrorMessage
                  name="password"
                  className="text-ircamprimary-accent"
                  component="span"
                />
              </label>
              <label
                htmlFor="password_confirmation"
                className="text-sm w-full my-4 block"
              >
                Confirm your password*
                <Field
                  as={Password}
                  id="password_confirmation"
                  name="password_confirmation"
                />
                <ErrorMessage
                  name="password_confirmation"
                  className="text-ircamprimary-accent"
                  component="span"
                />
              </label>
            </div>

            {false && (
              <div className="w-full my-4">
                <label
                  htmlFor="newsletter-signup"
                  className="text-sm text-primary-500"
                >
                  <Field
                    type="checkbox"
                    name="newsletter-signup"
                    className="rounded mr-4"
                    id="newsletter-signup"
                  />
                  Sign me up for the newsletter
                </label>
              </div>
            )}

            <div className="w-full my-4">
              <label htmlFor="legal_agree" className="text-sm text-primary-500">
                <Field
                  type="checkbox"
                  className="rounded mr-4"
                  name="legal_agree"
                  id="legal_agree"
                />
                Agree to our{' '}
                <a
                  href={import.meta.env.VITE_TERMS_CONDITIONS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="underline text-primary-950"
                >
                  terms and conditions
                </a>{' '}
                and our{' '}
                <a
                  href={import.meta.env.VITE_PRIVACY_POLICY_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="underline text-primary-950"
                >
                  privacy policy
                </a>
                *
                <ErrorMessage
                  name="legal_agree"
                  className="text-ircamprimary-accent block w-full"
                  component="span"
                />
              </label>
            </div>

            <div className="w-full text-center">
              <button
                type="submit"
                className="w-full button button-large button-dark py-2 px-4"
                disabled={loading}
              >
                <div className="button button-label">Sign up</div>
              </button>
            </div>
          </Form>
        </Formik>

        <div>
          <h3 className="text-sm text-primary-500 my-4">
            Already have an account ?{' '}
            <Link to="/login" className="font-bold hover:underline">
              Login
            </Link>
          </h3>
          <h3 className="text-sm text-primary-500 my-4">
            Need help ?{' '}
            <a
              id="intercom-signup"
              className="font-bold hover:underline"
              style={{ cursor: 'pointer' }}
            >
              Contact us
            </a>
          </h3>
        </div>
      </div>
      <AWSWAFCaptchaModal />
    </>
  )
}
